import { useState, useEffect, useRef } from "react"
import { useGlitchIntensity } from "../lib/useGlitchIntensity"
import { useGlitchEnabled } from "../lib/useGlitchEnabled"
import { getGlitchTiming, type GlitchTiming } from "../lib/glitchTiming"

// Unicode X variants — each is a different flavour of "close"
const X_CHARS = ['✕', '×', '✗', '✘', '⊗', '╳', '⨯', '☓', 'X', '⨉', '⊠', '✖'] as const

// Unicode hamburger / menu variants
const HAMBURGER_CHARS = ['☰', '≡', '⠿', '▤', '⊟', '⊞', '⋮', '⋯', '░'] as const

const X_COLORS = [
  'text-white/60',
  'text-red-400/70',
  'text-violet-400/60',
  'text-teal-400/60',
  'text-white/80',
  'text-luster-400/60',
]

const HAMBURGER_COLORS = [
  'text-white/60',
  'text-teal-400/60',
  'text-violet-400/60',
  'text-luster-400/60',
  'text-white/75',
]

interface GlitchToggleProps {
  isOpen: boolean
  className?: string
}

export function GlitchToggle({ isOpen, className = '' }: GlitchToggleProps) {
  const [glitchEnabled] = useGlitchEnabled()
  const [globalIntensity] = useGlitchIntensity()
  const effectiveIntensity = glitchEnabled ? globalIntensity : 0
  const timing = getGlitchTiming(effectiveIntensity)

  const chars = isOpen ? X_CHARS : HAMBURGER_CHARS
  const colors = isOpen ? X_COLORS : HAMBURGER_COLORS

  const [displayChar, setDisplayChar] = useState<string>(chars[0])
  const [displayColor, setDisplayColor] = useState<string>(colors[0])

  // Keep mutable refs so the ambient timer always reads fresh values
  const timingRef = useRef<GlitchTiming>(timing)
  timingRef.current = timing
  const charsRef = useRef<readonly string[]>(chars)
  charsRef.current = chars
  const colorsRef = useRef<readonly string[]>(colors)
  colorsRef.current = colors

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const holdRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearAll = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null }
  }

  // Snap back to base char when open state switches
  useEffect(() => {
    clearAll()
    const cs = isOpen ? X_CHARS : HAMBURGER_CHARS
    const cols = isOpen ? X_COLORS : HAMBURGER_COLORS
    setDisplayChar(cs[0])
    setDisplayColor(cols[0])
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  // Ambient glitch timer — only restarts when disabled toggles
  useEffect(() => {
    if (timingRef.current.disabled) {
      setDisplayChar(charsRef.current[0])
      setDisplayColor(colorsRef.current[0])
      return
    }

    const schedule = () => {
      const t = timingRef.current
      if (t.disabled) return
      const delay = t.minDelay + Math.random() * (t.maxDelay - t.minDelay)
      timerRef.current = setTimeout(() => {
        if (Math.random() < t.probability) {
          const cs = charsRef.current
          const cols = colorsRef.current
          const randomChar = cs[Math.floor(Math.random() * cs.length)]
          const randomColor = cols[Math.floor(Math.random() * cols.length)]
          setDisplayChar(randomChar)
          setDisplayColor(randomColor)

          const hold = t.holdMin + Math.random() * (t.holdMax - t.holdMin)
          holdRef.current = setTimeout(() => {
            setDisplayChar(charsRef.current[0])
            setDisplayColor(colorsRef.current[0])
            holdRef.current = null
          }, hold)
        }
        schedule()
      }, delay)
    }

    schedule()
    return clearAll
  }, [timing.disabled]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearAll(), []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <span
      className={`font-mono select-none leading-none ${displayColor} ${className}`}
      aria-hidden
    >
      {displayChar}
    </span>
  )
}
